import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { UserStatus } from '@core/generated/gql-types';
import { STATUS_OPTIONS, getStatusDisplay } from './worker.utils';

interface StatusDropdownProps {
  value: UserStatus | string;
  onChange: (status: UserStatus) => void;
  disabled?: boolean;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentStatus = getStatusDisplay(value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (status: string) => {
    onChange(status as UserStatus);
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${currentStatus.color} ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:opacity-80'}`}
      >
        {value === UserStatus.Active && (
          <FontAwesomeIcon icon={faCheck} className="h-3 w-3" />
        )}
        {currentStatus.label}
        <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3" />
      </button>

      {isOpen && (
        <div className="absolute left-0 z-50 mt-1 w-40 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
            >
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${option.color}`}
              >
                {option.value === UserStatus.Active && (
                  <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5" />
                )}
                {option.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatusDropdown;
